import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatEUR, formatDateFR, formatApiError } from "../../lib/api";
import { emitDataChanged } from "../../lib/dataBus";
import { toast } from "sonner";
import { Loader2, FileText, CheckCircle2, ArrowRight, Edit3, Trash2, Mail, Copy, Printer, Link2 } from "lucide-react";
import SegmentedPills from "../ui/SegmentedPills";
import { confirmAction } from "../ConfirmDialog";
import TabToolbar from "./TabToolbar";
import ViewToggle, { useViewMode } from "./ViewToggle";
import { FilterDropdown } from "./Filters";
import { CsvMenu } from "./CsvActions";
import EmptyState from "./EmptyState";
import { SkeletonCards, SkeletonTableRows } from "../ui/Skeleton";
import BusinessForm from "./BookingEditor";

const STATUS = {
  draft: { label: "Brouillon", cls: "bg-slate-100 text-slate-700" },
  sent: { label: "Envoyé", cls: "bg-blue-100 text-blue-800" },
  accepted: { label: "Accepté", cls: "bg-emerald-100 text-emerald-800" },
  declined: { label: "Refusé", cls: "bg-red-100 text-red-700" },
  expired: { label: "Expiré", cls: "bg-amber-100 text-amber-800" },
};

export default function DevisView({ vehicles, locations = [] }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Source de vérité : libellés de catégories depuis vendor_settings.fleet (id stable → label)
  const [fleetCategories, setFleetCategories] = useState([]);
  useEffect(() => {
    api.get("/vendor/settings").then((r) => setFleetCategories(r.data?.fleet?.categories || [])).catch(() => {});
  }, []);
  const catLabel = useMemo(() => {
    const m = Object.fromEntries((fleetCategories || []).map((c) => [c.value, c.label]));
    return (val) => m[val] || val;
  }, [fleetCategories]);

  const quoteParam = searchParams.get("quote"); // "new" | id | null
  const [viewMode, setViewMode] = useViewMode("quotes", "list");

  // Filtres transmis par le Copilote via l'URL (status, q, from, to)
  useEffect(() => {
    const s = searchParams.get("status");
    if (s && STATUS[s]) setStatusFilter(s);
    const q = searchParams.get("q");
    if (q && q.trim()) setSearch(q.trim().slice(0, 60));
    const f = searchParams.get("from");
    if (f && /^\d{4}-\d{2}-\d{2}$/.test(f)) setDateFrom(f);
    const t = searchParams.get("to");
    if (t && /^\d{4}-\d{2}-\d{2}$/.test(t)) setDateTo(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const load = useCallback(() => {
    api.get("/vendor/quotes")
      .then((r) => setQuotes(r.data.quotes || []))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditor = (id) => setSearchParams({ tab: "quotes", quote: id }, { replace: false });
  const closeEditor = () => setSearchParams({ tab: "quotes" }, { replace: false });

  const [sendingId, setSendingId] = useState(null);
  const sendByEmail = async (q) => {
    if (!window.confirm(`Envoyer le devis ${q.reference || ""} par email à ${q.client_email} ?`)) return;
    setSendingId(q.id);
    try {
      await api.post(`/vendor/quotes/${q.id}/send`);
      toast.success(`Devis envoyé à ${q.client_email}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible");
    }
    setSendingId(null);
  };

  const totals = useMemo(() => {
    const by = { draft: 0, sent: 0, accepted: 0, declined: 0, expired: 0 };
    quotes.forEach((q) => { by[q.status] = (by[q.status] || 0) + 1; });
    return by;
  }, [quotes]);

  const catByVehicle = useMemo(() => Object.fromEntries(vehicles.map((v) => [v.id, v.category || "Autre"])), [vehicles]);
  const categories = useMemo(() => [...new Set(vehicles.map((v) => v.category || "Autre"))].sort(), [vehicles]);

  const filtered = useMemo(() => {
    let list = statusFilter === "all" ? quotes : quotes.filter((q) => q.status === statusFilter);
    if (categoryFilter !== "all") {
      list = list.filter((q) => (q.vehicle_category || catByVehicle[q.vehicle_id] || "Autre") === categoryFilter);
    }
    if (dateFrom) list = list.filter((q) => q.end_date >= dateFrom);
    if (dateTo) list = list.filter((q) => q.start_date <= dateTo);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((q) => q.client_name?.toLowerCase().includes(s) || q.vehicle_name?.toLowerCase().includes(s) || q.client_email?.toLowerCase().includes(s) || q.reference?.toLowerCase().includes(s));
    }
    return list;
  }, [quotes, statusFilter, categoryFilter, dateFrom, dateTo, search, catByVehicle]);

  const changeStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/vendor/quotes/${id}`, { status });
      setQuotes(quotes.map((q) => q.id === id ? data.quote : q));
      emitDataChanged("quotes");
      toast.success("Statut mis à jour");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const remove = async (id) => {
    const q = quotes.find((x) => x.id === id);
    if (!(await confirmAction({
      title: "Supprimer définitivement ce devis ?",
      message: `Le devis ${q?.reference || ""} de ${q?.client_name || "ce client"} sera définitivement supprimé. Cette action est IRRÉVERSIBLE.`,
      confirmLabel: "Supprimer le devis",
      variant: "danger",
      requireText: "SUPPRIMER",
    }))) return;
    try {
      await api.delete(`/vendor/quotes/${id}`);
      setQuotes(quotes.filter((q) => q.id !== id));
      emitDataChanged("quotes");
      toast.success("Devis supprimé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  // ─── Pleine page : création / modification ───
  if (quoteParam) {
    const existing = quoteParam === "new" ? null : quotes.find((q) => q.id === quoteParam);
    if (quoteParam !== "new" && loading) {
      return <SkeletonTableRows rows={6} cols={6} testid="quote-editor-skeleton" />;
    }
    return (
      <QuoteEditor
        key={quoteParam}
        vehicles={vehicles}
        locations={locations}
        quote={existing}
        onBack={closeEditor}
        onSaved={() => { load(); closeEditor(); emitDataChanged("quotes"); }}
        onDeleted={() => { load(); closeEditor(); emitDataChanged("quotes"); }}
        onAccepted={(booking) => {
          load();
          emitDataChanged(["quotes", "bookings"]);
          if (booking?.id) navigate(`/vendor/pilotage?tab=bookings&booking=${booking.id}`);
          else closeEditor();
        }}
      />
    );
  }

  // ─── Liste ───
  return (
    <div className="h-full overflow-y-auto p-4 sm:p-6" data-testid="pilotage-quotes">
      <div className="w-full">
        {/* Hidden trigger (activé par le + global) */}
        <button onClick={() => openEditor("new")} className="hidden" data-testid="create-quote-btn" aria-hidden="true" tabIndex={-1}>hidden</button>

        <TabToolbar
          title="Devis"
          count={quotes.length}
          search={search}
          onSearch={setSearch}
          searchPlaceholder="Rechercher un devis..."
          searchTestid="search-quotes"
          filtersActive={(statusFilter !== "all" ? 1 : 0) + (categoryFilter !== "all" ? 1 : 0) + ((dateFrom || dateTo) ? 1 : 0)}
          filters={
            <div className="flex flex-wrap items-center gap-2">
              <SegmentedPills
                items={[
                  { key: "all", label: "Tous", count: quotes.length },
                  { key: "draft", label: "Brouillons", count: totals.draft || 0 },
                  { key: "sent", label: "Envoyés", count: totals.sent || 0 },
                  { key: "accepted", label: "Acceptés", count: totals.accepted || 0 },
                  { key: "declined", label: "Refusés", count: totals.declined || 0 },
                  { key: "expired", label: "Expirés", count: totals.expired || 0 },
                ]}
                value={statusFilter}
                onChange={setStatusFilter}
                testid="quotes-filter"
                variant="pills"
              />
              {categories.length > 1 && (
                <FilterDropdown
                  label="Catégorie"
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  options={[{ value: "all", label: "Toutes les catégories" }, ...categories.map((c) => ({ value: c, label: catLabel(c) }))]}
                  testid="quotes-category-filter"
                />
              )}
              <div className="flex flex-wrap items-center gap-1.5" data-testid="quotes-date-filter">
                <span className="text-[10px] uppercase font-bold text-slate-400">Période</span>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10 px-2.5 rounded-xl border border-slate-200 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-violet-500" data-testid="quotes-date-from" />
                <span className="text-xs text-slate-400">→</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 px-2.5 rounded-xl border border-slate-200 bg-white text-xs focus:outline-none focus:ring-2 focus:ring-violet-500" data-testid="quotes-date-to" />
                {(dateFrom || dateTo) && (
                  <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-[10px] font-semibold text-violet-600 hover:underline" data-testid="quotes-date-clear">Effacer</button>
                )}
              </div>
            </div>
          }
          viewToggle={<ViewToggle value={viewMode} onChange={setViewMode} testid="quotes-view-toggle" />}
          addLabel="Devis"
          onAdd={() => openEditor("new")}
          addTestid="add-quote-btn"
          permissionModule="quotes"
          extra={
            <CsvMenu exportPath="/vendor/quotes/export" exportFilename="devis.csv" testid="quotes-csv-menu" />
          }
        />

        <div className="mt-5">
        {loading ? (
          viewMode === "cards"
            ? <SkeletonCards count={8} testid="quotes-skeleton-cards" />
            : <SkeletonTableRows rows={7} cols={6} testid="quotes-skeleton-rows" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FileText}
            title={quotes.length === 0 ? "Aucun devis pour l'instant" : "Aucun devis ne correspond"}
            description={quotes.length === 0 ? "Créez votre premier devis et envoyez-le à un client en quelques clics." : "Essayez d'ajuster votre recherche ou vos filtres."}
            actionLabel={quotes.length === 0 ? "Nouveau devis" : undefined}
            onAction={quotes.length === 0 ? () => openEditor("new") : undefined}
            testid="quotes-empty"
          />
        ) : viewMode === "cards" ? (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3" data-testid="quotes-cards">
            {filtered.map((q) => (
              <button key={q.id} onClick={() => q.status === "accepted" && q.booking_id ? navigate(`/vendor/pilotage?tab=bookings&booking=${q.booking_id}`) : openEditor(q.id)} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-violet-300 hover:shadow-md transition-all text-left" data-testid={`quote-card-${q.id}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS[q.status]?.cls || "bg-slate-100"}`}>{STATUS[q.status]?.label}</span>
                  <span className="text-sm font-bold text-slate-900">{formatEUR(q.total_ttc)}</span>
                </div>
                <div className="font-heading text-sm font-semibold text-slate-900 truncate">{q.client_name}</div>
                <div className="text-xs text-slate-500 truncate">{q.vehicle_name}{q.reference && <span className="ml-1.5 font-mono text-[10px] text-slate-400">{q.reference}</span>}</div>
                <div className="mt-2 text-xs text-slate-500">{formatDateFR(q.start_date)} → {formatDateFR(q.end_date)} · {q.days} j · TVA {q.vat_rate}%</div>
                {q.valid_until && q.status !== "accepted" && (
                  <div className={`mt-1 text-[10px] ${q.status === "expired" ? "text-amber-600 font-semibold" : "text-slate-400"}`}>
                    {q.status === "expired" ? "Expiré depuis le" : "Valide jusqu'au"} {formatDateFR(q.valid_until)}
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((q) => (
              <div key={q.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-slate-300 transition-colors" data-testid={`quote-row-${q.id}`}>
                <button onClick={() => q.status === "accepted" && q.booking_id ? navigate(`/vendor/pilotage?tab=bookings&booking=${q.booking_id}`) : openEditor(q.id)} className="flex-1 min-w-0 text-left" data-testid={`quote-open-${q.id}`}>
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {q.client_name} · {q.vehicle_name}
                    {q.reference && <span className="ml-1.5 font-mono text-[10px] text-slate-400" data-testid={`quote-ref-${q.id}`}>{q.reference}</span>}
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatDateFR(q.start_date)} → {formatDateFR(q.end_date)} · {q.days} jour{q.days > 1 ? "s" : ""} · TVA {q.vat_rate}%
                    {q.valid_until && q.status !== "accepted" && (
                      <span className={`ml-1.5 ${q.status === "expired" ? "text-amber-600 font-semibold" : "text-slate-400"}`}>
                        · {q.status === "expired" ? "expiré le" : "valide jusqu'au"} {formatDateFR(q.valid_until)}
                      </span>
                    )}
                  </div>
                </button>
                <div className="text-sm font-semibold text-slate-900 shrink-0">{formatEUR(q.total_ttc)} TTC</div>
                <select
                  value={q.status}
                  onChange={(e) => changeStatus(q.id, e.target.value)}
                  className={`text-xs font-semibold rounded-lg border-0 px-2 py-1.5 cursor-pointer ${STATUS[q.status]?.cls || ""}`}
                  data-testid={`quote-status-${q.id}`}
                  disabled={q.status === "accepted" && !!q.booking_id}
                >
                  {q.status === "expired" && <option value="expired" disabled>Expiré</option>}
                  {Object.entries(STATUS).filter(([k]) => k !== "expired").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                {q.status !== "accepted" && (
                  <button onClick={() => openEditor(q.id)} className="h-8 px-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 inline-flex items-center gap-1 text-[11px] font-medium shrink-0" title="Modifier" data-testid={`quote-edit-${q.id}`}>
                    <Edit3 className="h-3 w-3" /> Modifier
                  </button>
                )}
                {!["accepted", "declined"].includes(q.status) && (
                  <button
                    onClick={() => sendByEmail(q)}
                    disabled={sendingId === q.id}
                    className="h-8 px-2.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white inline-flex items-center gap-1 text-[11px] font-semibold shrink-0 disabled:opacity-50"
                    title={`Envoyer par email à ${q.client_email}`}
                    data-testid={`quote-send-email-${q.id}`}
                  >
                    {sendingId === q.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />} Envoyer
                  </button>
                )}
                {q.status === "accepted" && q.booking_id && (
                  <button onClick={() => navigate(`/vendor/pilotage?tab=bookings&booking=${q.booking_id}`)} className="h-8 px-3 rounded-lg bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1 text-[11px] font-semibold shrink-0" data-testid={`quote-open-booking-${q.id}`}>
                    Voir la résa <ArrowRight className="h-3 w-3" />
                  </button>
                )}
                <button onClick={() => remove(q.id)} className="h-8 w-8 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center shrink-0" title="Supprimer" data-testid={`quote-delete-${q.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

// ─── Éditeur de devis en pleine page : réutilise le formulaire métier unifié ──
function QuoteEditor({ vehicles, locations, quote, onBack, onSaved, onDeleted, onAccepted }) {
  const isEdit = !!quote;
  const [accepting, setAccepting] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convTimes, setConvTimes] = useState({ start_time: "", end_time: "" });
  const [convErr, setConvErr] = useState("");

  const remove = async () => {
    if (!(await confirmAction({
      title: "Supprimer définitivement ce devis ?",
      message: `Le devis ${quote.reference || ""} de ${quote.client_name} sera définitivement supprimé. Cette action est IRRÉVERSIBLE.`,
      confirmLabel: "Supprimer le devis",
      variant: "danger",
      requireText: "SUPPRIMER",
    }))) return;
    try {
      await api.delete(`/vendor/quotes/${quote.id}`);
      toast.success("Devis supprimé");
      onDeleted();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/vendor/quotes/${quote.id}/duplicate`);
      toast.success(`Devis dupliqué : ${data.quote.reference}`);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const copyClientLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/quote/${quote.id}`);
    toast.success("Lien client copié — le client pourra accepter ou refuser en ligne");
  };

  const accept = () => {
    setConvErr("");
    setConvTimes({ start_time: "", end_time: "" });
    setConvertOpen(true);
  };

  const confirmConvert = async () => {
    const st = (convTimes.start_time || "").trim();
    const et = (convTimes.end_time || "").trim();
    if (!st) { setConvErr("L'heure de départ est obligatoire."); return; }
    if (!et) { setConvErr("L'heure de retour est obligatoire."); return; }
    if (quote.start_date === quote.end_date && et <= st) {
      setConvErr("L'heure de retour doit être postérieure à l'heure de départ pour une réservation sur une seule journée.");
      return;
    }
    setConvErr("");
    setAccepting(true);
    try {
      const { data } = await api.post(`/vendor/quotes/${quote.id}/accept`, { start_time: st, end_time: et });
      toast.success("Devis accepté — réservation créée");
      setConvertOpen(false);
      onAccepted(data.booking);
    } catch (err) {
      setConvErr(formatApiError(err.response?.data?.detail) || "Erreur");
      setAccepting(false);
    }
  };

  const st = STATUS[quote?.status];
  const badge = st ? <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${st.cls}`} data-testid="quote-status-badge">{st.label}</span> : null;

  const headerActions = isEdit ? (
    <>
      <button
        onClick={copyClientLink}
        className="h-10 px-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:border-violet-300 transition-colors inline-flex items-center gap-1.5"
        title="Lien public : le client accepte ou refuse en ligne"
        data-testid="quote-copy-link-btn"
      >
        <Link2 className="h-3.5 w-3.5" /> Lien client
      </button>
      <button
        onClick={duplicate}
        className="h-10 px-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:border-violet-300 transition-colors inline-flex items-center gap-1.5"
        data-testid="quote-duplicate-btn"
      >
        <Copy className="h-3.5 w-3.5" /> Dupliquer
      </button>
      <button
        onClick={() => window.print()}
        className="h-10 px-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:border-violet-300 transition-colors inline-flex items-center gap-1.5"
        data-testid="quote-print-btn"
      >
        <Printer className="h-3.5 w-3.5" /> Imprimer
      </button>
      {quote.status !== "declined" && (
        <button
          onClick={accept}
          disabled={accepting}
          className="h-10 px-4 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="quote-accept-btn"
        >
          {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Accepter
        </button>
      )}
      <button
        onClick={remove}
        className="h-10 px-4 rounded-xl border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors inline-flex items-center gap-2"
        data-testid="quote-delete-editor-btn"
      >
        <Trash2 className="h-3.5 w-3.5" /> Supprimer
      </button>
    </>
  ) : null;

  const topBanner = isEdit ? (
    <>
      {quote.client_response && (
        <div className={`mt-4 rounded-2xl border p-4 text-sm ${quote.client_response.action === "accept" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`} data-testid="quote-client-response">
          <div className="font-semibold">
            {quote.client_response.action === "accept" ? "Accepté par le client en ligne" : "Refusé par le client en ligne"}
            {quote.client_response.responded_at && <span className="font-normal"> · {formatDateFR(quote.client_response.responded_at.slice(0, 10))}</span>}
          </div>
          {quote.client_response.comment && <p className="mt-1 text-xs opacity-80">« {quote.client_response.comment} »</p>}
          <p className="mt-1 text-[10px] opacity-60">Preuve : IP {quote.client_response.ip_address} · {quote.client_response.responded_at}</p>
        </div>
      )}
      {/* Zone d'impression (visible uniquement à l'impression) */}
      <div id="quote-print-area" className="hidden print:block">
        <div className="font-heading text-2xl font-medium">Devis {quote.reference}</div>
        <div className="text-sm mt-1">Créé le {formatDateFR((quote.created_at || "").slice(0, 10))}{quote.valid_until ? ` — valable jusqu'au ${formatDateFR(quote.valid_until)}` : ""}</div>
        <div className="mt-4 text-sm">
          <div><strong>Client :</strong> {quote.client_name} · {quote.client_email}{quote.client_phone ? ` · ${quote.client_phone}` : ""}</div>
          <div className="mt-1"><strong>Véhicule :</strong> {quote.vehicle_name}</div>
          <div className="mt-1"><strong>Période :</strong> du {formatDateFR(quote.start_date)} au {formatDateFR(quote.end_date)} ({quote.days} jour{quote.days > 1 ? "s" : ""})</div>
        </div>
        <table className="w-full mt-4 text-sm" style={{ borderCollapse: "collapse" }}>
          <tbody>
            <tr><td style={{ padding: "4px 0" }}>Location ({quote.days} j × {formatEUR(quote.price_per_day)})</td><td style={{ textAlign: "right" }}>{formatEUR((quote.subtotal != null ? quote.subtotal : quote.days * quote.price_per_day))}</td></tr>
            {(quote.options || []).map((o) => <tr key={o.key || o.label}><td style={{ padding: "4px 0" }}>{o.label}</td><td style={{ textAlign: "right" }}>+ {formatEUR(o.amount)}</td></tr>)}
            {(quote.applied_surcharges || []).filter((s) => s.state === "applied").map((s) => (
              <tr key={`${s.surcharge_id}-${s.event}`} data-testid={`quote-surcharge-${s.event}-${s.surcharge_id}`}><td style={{ padding: "4px 0" }}>{s.event === "departure" ? "Départ" : "Retour"} — {s.name}</td><td style={{ textAlign: "right" }}>+ {formatEUR(s.amount)}</td></tr>
            ))}
            {quote.discount_amount > 0 && <tr><td style={{ padding: "4px 0" }}>Remise</td><td style={{ textAlign: "right" }}>− {formatEUR(quote.discount_amount)}</td></tr>}
            <tr><td style={{ padding: "4px 0" }}>Total HT</td><td style={{ textAlign: "right" }}>{formatEUR(quote.total_ht)}</td></tr>
            <tr><td style={{ padding: "4px 0" }}>TVA {quote.vat_rate} %</td><td style={{ textAlign: "right" }}>{formatEUR(quote.vat_amount)}</td></tr>
            <tr style={{ fontWeight: 600, borderTop: "1px solid #000" }}><td style={{ padding: "6px 0" }}>Total TTC</td><td style={{ textAlign: "right" }}>{formatEUR(quote.total_ttc)}</td></tr>
          </tbody>
        </table>
        {quote.notes && <div className="mt-4 text-xs whitespace-pre-wrap">{quote.notes}</div>}
      </div>
    </>
  ) : null;

  return (
    <>
      <BusinessForm
        mode="quote"
        record={quote}
        vehicles={vehicles}
        locations={locations}
        onBack={onBack}
        onSaved={onSaved}
        headerActions={headerActions}
        topBanner={topBanner}
        badge={badge}
      />

      {convertOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="convert-quote-modal">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => !accepting && setConvertOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl p-6">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Convertir ce devis en réservation</h3>
            <p className="mt-1.5 text-[13px] text-slate-500 leading-relaxed">
              Le devis {quote.reference || ""} ({formatEUR(quote.total_ttc)} TTC) sera accepté et une réservation confirmée créée pour {quote.client_name}. Renseignez l'heure de départ et l'heure de retour.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Heure de départ<span className="text-red-500 ml-0.5">*</span></label>
                <input type="time" value={convTimes.start_time} onChange={(e) => { setConvTimes((t) => ({ ...t, start_time: e.target.value })); if (convErr) setConvErr(""); }} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none" data-testid="convert-start-time" />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Heure de retour<span className="text-red-500 ml-0.5">*</span></label>
                <input type="time" value={convTimes.end_time} onChange={(e) => { setConvTimes((t) => ({ ...t, end_time: e.target.value })); if (convErr) setConvErr(""); }} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-600 outline-none" data-testid="convert-end-time" />
              </div>
            </div>
            {convErr && <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="convert-error">{convErr}</div>}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setConvertOpen(false)} disabled={accepting} className="px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-60" data-testid="convert-cancel">Annuler</button>
              <button type="button" onClick={confirmConvert} disabled={accepting} className="inline-flex items-center gap-2 px-4 py-2 rounded-full btn-primary text-sm font-semibold disabled:opacity-60" data-testid="convert-confirm">
                {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirmer la conversion
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
